// THE STANDARD - Client Trench Layout
// Mobile-first client shell with four calm navigation anchors.
// iPhone/iPad optimized: safe areas, 100dvh, momentum scroll, touch targets

import { useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useLocation, useSearch } from 'wouter';
import { mapClientLocationToCoach } from '@/config/standardRoutes';
import { WorkspaceSwitch } from './WorkspaceSwitch';
import { usePhoneLayout } from './usePhoneLayout';
import { PhoneMorePage } from './PhoneMorePage';
import { BackChevronGlyph, MessageGlyph, TrophyGlyph } from './TabIcons';
import type { ComponentProps } from 'react';
import { LayoutDashboard, MoreHorizontal, X, Zap, Bell, BellOff, BellRing, LogOut, ArrowLeft, Calendar, BookOpen, PenLine, Flame, MessageSquare, ShieldCheck, Newspaper, Video, History, Ruler, CreditCard, UserCircle, ClipboardList, Users, Trophy, Microscope, BookHeart, BookMarked, Dumbbell, Utensils, Camera, CheckSquare, ListTodo, Tag, Cross, Activity, Sparkles, GraduationCap, HeartPulse, ChevronDown, ChevronRight, LayoutGrid, UserCheck } from 'lucide-react';
import { useGuidedTour } from '@/components/GuidedTour';
import { hasInPersonNavigation, clientBottomTargets, clientPhoneTabs, clientTabActive } from '@shared/clientNavigation';
import { ClientCustomBottomNav } from './ClientCustomBottomNav';
import { WidePrimaryNav } from './WidePrimaryNav';
import { trpc } from '@/lib/trpc';
import { setNativeSessionToken } from '@/lib/apiBase';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import NotificationBell from '@/components/NotificationBell';
import PushNotificationBanner from '@/components/PushNotificationBanner';
import IOSInstallBanner from '@/components/IOSInstallBanner';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import { SubscriptionGate } from '@/components/SubscriptionGate';
import { OfflineBanner } from '@/components/OfflineBanner';
import { ActiveWorkoutPill } from '@/components/ActiveWorkoutPill';
import { ActiveChallengeBanner } from '@/components/challenges/ActiveChallengeBanner';
import { useOfflineSync } from '@/hooks/useOfflineSync';
import { useBack } from '@/hooks/useBack';
import { shouldShowClientShellBack } from '@/lib/clientNavigationHistory';
import { recordAppLocation } from '@/lib/appNavigationHistory';
import { PremiumSlimBar } from './PremiumSlimBar';
import { useSlimChrome } from './useSlimChrome';
import { shouldOpenDirectory } from './directoryShortcut';
import '@/styles/responsive-workspace.css';
import { STANDARD_LOGO_SRC } from '@/lib/brandAssets';
import { toast } from 'sonner';
import { Capacitor } from '@capacitor/core';
import {
  CLIENT_MORE_SECTIONS as REGISTERED_CLIENT_MORE_SECTIONS,
  getStandardRouteTitle,
} from '@/config/standardRoutes';
import '@/styles/premium-app-shell.css';
import '@/styles/system-workspace.css';
import '@/styles/ipad-desktop-workspace.css';
import '@/styles/standard-shell.css';
import '@/styles/iphone-shell.css';
// Phone-only fluid type and spacing; keep it after the other shell styles.
import '@/styles/phone-fluid.css';
// Phone-only page surface (one background, paper cards); after phone-fluid.
import '@/styles/phone-surfaces.css';
// Member screen touch targets (client app and /coach/my).
import '@/styles/member-screens.css';
// Glass on the floating chrome only; switch in ./glassChrome. Keep it last.
import '@/styles/glass-chrome.css';
import { useGlassChrome } from './glassChrome';

// Generated from the typed route registry so labels, descriptions, icons, and
// exposure stay aligned with titles, bottom-nav choices, and coach equivalents.
const MORE_SECTIONS = REGISTERED_CLIENT_MORE_SECTIONS.map((section) => ({
  label: section.label,
  items: section.items.map((route) => ({
    path: route.path,
    label: route.menuLabel,
    desc: route.menuDescription,
    icon: route.icon!,
  })),
}));

// Flat list for active-state detection.
const MORE_ITEMS = MORE_SECTIONS.flatMap((s) => s.items);

const MORE_SECTION_META = {
  Coaching: { icon: UserCheck, description: 'Schedule, reviews, tasks, and reports' },
  Training: { icon: Dumbbell, description: 'Workouts, programs, history, and tools' },
  Nutrition: { icon: Utensils, description: 'Food log, plan, hydration, and progress' },
  Faith: { icon: BookHeart, description: 'Formation, Scripture, journal, and courses' },
  Community: { icon: Users, description: 'Posts, challenges, groups, and wins' },
  Account: { icon: UserCircle, description: 'Profile, preferences, billing, and help' },
} as const;

function getPageTitle(path: string): string {
  return getStandardRouteTitle(path);
}

function PushToggleButton() {
  const { isSupported, isSubscribed, isLoading, toggle } = usePushNotifications();
  if (!isSupported) return null;
  return (
    <button onClick={toggle} disabled={isLoading} className="flex min-h-[58px] w-full items-center justify-center gap-2 rounded-xl border border-[#d7dcdf] bg-white p-3 text-[#2f5d73] transition-all active:opacity-60 disabled:opacity-50">
      {isSubscribed ? <Bell className="w-5 h-5" strokeWidth={1.5} /> : <BellOff className="w-5 h-5" strokeWidth={1.5} />}
      <span className="text-xs font-black leading-none text-center">
        {isLoading ? '...' : isSubscribed ? 'Notifs On' : 'Notifs Off'}
      </span>
    </button>
  );
}

interface ClientLayoutProps {
  children: React.ReactNode;
  coachPersonal?: boolean;
}

export default function ClientLayout(props: ClientLayoutProps) {
  const recovery = <div className="min-h-[100dvh] bg-[#f2f0ec]">
    <header style={{ paddingTop: 'max(12px, env(safe-area-inset-top, 0px))' }} className="border-b border-[#d5d0c6] px-5 py-3"><RouterLink href="/client" className="inline-flex min-h-11 items-center gap-2 font-semibold text-[#2f5d73]"><ArrowLeft className="size-4" aria-hidden="true" />Back to account access</RouterLink></header>
    {props.children}
  </div>;
  return <SubscriptionGate recoveryChildren={recovery}><ClientLayoutContent {...props} /></SubscriptionGate>;
}

function ClientLayoutContent({ children, coachPersonal = false }: ClientLayoutProps) {
  const [actualLocation, actualNavigate] = useLocation();
  const actualSearch = useSearch();
  const location = coachPersonal ? (actualLocation.replace('/coach/my/today', '/client').replace('/coach/my', '/client').replace('/coach/wins', '/client/wins')) : actualLocation;
  const hrefFor = (href: string) => coachPersonal && href.startsWith('/client') ? mapClientLocationToCoach(href) : href;
  const navigate = (href: string) => actualNavigate(hrefFor(href));
  const Link = ({ href, ...props }: React.AnchorHTMLAttributes<HTMLAnchorElement>) => <RouterLink {...props} href={hrefFor(href ?? '')} />;
  const goBack = useBack(coachPersonal ? '/coach/my/today' : '/client');
  const phone = usePhoneLayout();
  const [showMore, setShowMore] = useState(false);
  useEffect(() => setShowMore(false), [location]);
  const [expandedMoreSection, setExpandedMoreSection] = useState<string | null>('Coaching');
  const [logoRefreshing, setLogoRefreshing] = useState(false);
  const guidedTour = useGuidedTour();
  const isNativeApp = Capacitor.isNativePlatform();
  const { isSupported: pushSupported, isLoading: pushLoading, subscribe: subscribePush } = usePushNotifications();

  // ── Offline sync engine: drains queued mutations when connectivity returns ──
  useOfflineSync();
  const handleLogoRefresh = () => {
    if (logoRefreshing) return;
    setLogoRefreshing(true);
    setTimeout(async () => {
      try {
        if ('caches' in window) {
          const keys = await (window as Window & typeof globalThis).caches.keys();
          await Promise.all(keys.map(k => (window as Window & typeof globalThis).caches.delete(k)));
        }
      } catch { /* best-effort */ }
      sessionStorage.setItem('app_just_refreshed', '1');
      window.location.reload();
    }, 300);
  };
  // Show "App updated" toast after a logo-tap refresh
  useEffect(() => {
    if (sessionStorage.getItem('app_just_refreshed')) {
      sessionStorage.removeItem('app_just_refreshed');
      setTimeout(() => toast('App updated', { duration: 3000 }), 500);
    }
  }, []);
  const { data: user } = trpc.auth.me.useQuery(undefined, { staleTime: 5 * 60_000 });
  const { data: scopedAccess } = trpc.community.getMyScopedAccess.useQuery(undefined, {
    enabled: Boolean(user) && user?.role !== 'admin' && user?.role !== 'coach',
    retry: false,
  });
  const { data: navigationProfile } = trpc.clients.myProfile.useQuery(undefined, { enabled: Boolean(user), staleTime: 60_000 });
  const { data: navigationMembership } = trpc.standardMembership.getMyState.useQuery(undefined, { enabled: Boolean(user), staleTime: 60_000, retry: false });
  const inPersonNavigation = navigationMembership !== undefined && hasInPersonNavigation(navigationProfile?.coachingType, Boolean(navigationMembership?.selfDirectedOnly));
  const isCoach = user?.role === 'admin' || user?.role === 'coach';
  const showPrivateCoaching = isCoach || (navigationMembership !== undefined && !navigationMembership?.selfDirectedOnly);
  const { data: unreadMessages = 0 } = trpc.messages.unreadCount.useQuery(undefined, {
    staleTime: 30_000,
    refetchInterval: 30_000,
  });

  useEffect(() => {
    if (!isNativeApp) return;
    if (!pushSupported || pushLoading) return;
    const sessionKey = 'client_native_push_registered_this_session';
    if (sessionStorage.getItem(sessionKey) === '1') return;
    sessionStorage.setItem(sessionKey, '1');
    subscribePush().catch(() => { /* native push registration is best-effort */ });
  }, [isNativeApp, pushSupported, pushLoading, subscribePush]);

  // App.OnboardingGate owns intake routing synchronously. A second redirect
  // here raced the value-first flow and membership activation routes.

  const logout = trpc.auth.logout.useMutation({
    onSuccess: () => {
      setNativeSessionToken(null);
      toast.success('Signed out');
      window.location.href = '/login';
    },
    onError: () => {
      setNativeSessionToken(null);
      // Force redirect even if mutation errors
      window.location.href = '/login';
    },
  });

  // Phone: only the four tab roots count as main; a workout or a reading
  // screen under a tab gets Back in the dock and from Android's back button.
  const isMainTab = phone
    ? clientPhoneTabs().some(tab => location.split('?')[0] === tab.path)
    : clientBottomTargets(user?.id ?? 0, inPersonNavigation).some(tab => clientTabActive(tab.path, location));
  // Only light up More for non-primary routes surfaced in the drawer.
  const isCommsRoute = location.startsWith('/client/comms');
  const isOnboardingRoute = location.startsWith('/client/onboarding');
  const showBackButton = !isMainTab && shouldShowClientShellBack(location);
  // iPhone dock: root tabs and the More page show the brand; other screens
  // show Back in its place.
  const phoneDockBack = phone && !isOnboardingRoute && !isMainTab && !showMore;
  const showDockLogo = phone ? !phoneDockBack : !showBackButton;
  const shellRef = useRef<HTMLDivElement>(null);
  useSlimChrome(shellRef);
  useGlassChrome(shellRef);

  useEffect(() => {
    recordAppLocation(actualLocation + (actualSearch ? `?${actualSearch}` : ''));
  }, [actualLocation, actualSearch]);

  // Android's hardware back button follows the same in-app history as the
  // visible shell control. The home and onboarding flows retain Capacitor's
  // normal root/flow behavior. An open More page also takes the back press so
  // it closes before the app leaves the current screen.
  useEffect(() => {
    if (!isNativeApp || (!showBackButton && !showMore)) return;

    let disposed = false;
    let removeListener: (() => Promise<void>) | undefined;
    void import('@capacitor/app')
      .then(({ App }) => App.addListener('backButton', () => {
        if (showMore) setShowMore(false);
        else goBack();
      }))
      .then((handle) => {
        if (disposed) void handle.remove();
        else removeListener = () => handle.remove();
      })
      .catch(() => { /* hardware back support is best-effort */ });

    return () => {
      disposed = true;
      if (removeListener) void removeListener();
    };
  }, [goBack, isNativeApp, showBackButton, showMore]);

  // Ctrl+K or Cmd+K opens the app directory from anywhere in the shell,
  // except while typing in a field or working inside a dialog.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (shouldOpenDirectory(event)) {
        event.preventDefault();
        setShowMore(true);
      } else if (event.key === 'Escape' && showMore) {
        setShowMore(false);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [showMore]);

  return (
    <div
      ref={shellRef}
      className="premium-app-shell premium-client-shell wide-shell flex flex-col overflow-hidden kb-shell"
      style={{ background: 'var(--premium-canvas)' }}
    >
      {!isOnboardingRoute && (
        <WidePrimaryNav
          location={location}
          hrefFor={hrefFor}
          user={user}
          workspaceMode={isCoach ? 'athlete' : undefined}
          onAllTools={() => setShowMore(true)}
          onBrandClick={handleLogoRefresh}
          showMessages={showPrivateCoaching}
          unreadMessages={unreadMessages as number}
        />
      )}
      <div className="wide-shell-main">
      <OfflineBanner />
      <PremiumSlimBar
        className={`standard-shell-topbar glass-top-bar flex-shrink-0${phone ? ' iphone-dock' : ''}${phoneDockBack ? ' iphone-dock--back' : ''}`}
        style={{
          paddingTop: 'max(12px, env(safe-area-inset-top, 0px))',
          paddingBottom: '6px',
        }}
        showBack={phone ? phoneDockBack : !isOnboardingRoute && location !== '/client'}
        onBack={goBack}
        backGlyph={phone ? <BackChevronGlyph size={22} /> : undefined}
        // On a touch phone the Today tab replaces Dashboard; a narrow desktop
        // window hides the touch tab bar, so iphone-shell.css shows it there.
        onDashboard={!isOnboardingRoute ? () => navigate('/client') : undefined}
        dashboardCurrent={location === '/client'}
        // Coach and admin accounts: the one Training and Command switch, in
        // the same place as on the Command bar. Members never see it.
        modeSwitch={isCoach ? <WorkspaceSwitch mode="athlete" /> : undefined}
        onSearch={!isOnboardingRoute && !phone ? () => setShowMore(true) : undefined}
      >
        {showDockLogo && (
          <button type="button" onClick={handleLogoRefresh} title="Tap to refresh app" className="standard-topbar-logo flex items-center gap-2">
            <img
              src={STANDARD_LOGO_SRC}
              alt="The Standard"
              className={`h-7 w-auto object-contain select-none transition-opacity ${logoRefreshing ? 'opacity-30' : 'active:opacity-50'}`}
            />
            {phone
              ? <span className="iphone-dock__word" aria-hidden="true">THE STANDARD</span>
              : <span className="standard-member-brand hidden sm:flex"><strong>THE STANDARD</strong><span>Live the work.</span></span>}
          </button>
        )}
        {getPageTitle(location) ? <span className="sr-only">{getPageTitle(location)}</span> : null}
        <nav className="premium-client-desktop-shortcuts hidden lg:flex items-center justify-center gap-1" aria-label="Community shortcuts">
          <Link href="/client/community">
            <button className="inline-flex items-center gap-2 px-3 text-xs font-black uppercase tracking-wider" style={{ color: location.startsWith('/client/community') ? 'var(--primary)' : '#c8c5bd', fontFamily: 'Barlow Condensed, sans-serif' }}>
              <Users className="w-4 h-4" /> Community
            </button>
          </Link>
          <Link href="/client/community?view=groups">
            <button className="inline-flex items-center gap-2 px-3 text-xs font-black uppercase tracking-wider" style={{ color: '#c8c5bd', fontFamily: 'Barlow Condensed, sans-serif' }}>
              <ShieldCheck className="w-4 h-4" /> Groups
            </button>
          </Link>
          {user && (
            <Link href={`/client/members/${user.id}`}>
              <button className="inline-flex items-center gap-2 px-3 text-xs font-black uppercase tracking-wider" style={{ color: location.startsWith(`/client/members/${user.id}`) ? 'var(--primary)' : '#c8c5bd', fontFamily: 'Barlow Condensed, sans-serif' }}>
                <UserCircle className="w-4 h-4" /> My Profile
              </button>
            </Link>
          )}
        </nav>
        {phone ? (
          <>
            {!phoneDockBack && (
              <Link
                href="/client/challenges"
                aria-label="Challenge HQ"
                title="Challenge HQ"
                className="iphone-dock__ib"
                data-active={location.startsWith('/client/challenges') || location.startsWith('/client/challenge-program/') ? 'true' : undefined}
              >
                <TrophyGlyph size={21} />
              </Link>
            )}
            {showPrivateCoaching && (
              <Link
                href="/client/comms"
                data-tour="messages-icon"
                aria-label={(unreadMessages as number) > 0 ? `Message Coach Fe, ${unreadMessages} unread` : 'Message Coach Fe'}
                title="Message Coach Fe"
                className="iphone-dock__ib"
                data-active={isCommsRoute ? 'true' : undefined}
              >
                <MessageGlyph size={21} />
                {(unreadMessages as number) > 0 ? <i aria-hidden="true">{(unreadMessages as number) > 9 ? '9+' : unreadMessages}</i> : null}
              </Link>
            )}
            <span className="iphone-dock__bell"><NotificationBell /></span>
            <button type="button" onClick={() => setShowMore(!showMore)} aria-label="More app sections" aria-expanded={showMore} className="iphone-dock__ib iphone-dock__more">
              <LayoutGrid className="h-5 w-5" aria-hidden="true" />
            </button>
          </>
        ) : <>
        <Link href="/client/challenges">
          <button
            title="Challenge HQ"
            aria-label="Open Challenge HQ"
            className="standard-topbar-icon relative hidden sm:flex items-center justify-center w-9 h-9 rounded-xl transition-colors active:opacity-60"
            data-active={location.startsWith('/client/challenges') || location.startsWith('/client/challenge-program/')}
          >
            <Trophy className="w-5 h-5" strokeWidth={1.5} />
          </button>
        </Link>
        {showPrivateCoaching && <Link href="/client/comms">
          <button
            data-tour="messages-icon"
            title="Message Coach Fe"
            aria-label={(unreadMessages as number) > 0 ? `Message Coach Fe, ${unreadMessages} unread` : 'Message Coach Fe'}
            className="standard-topbar-icon relative flex items-center justify-center w-9 h-9 rounded-xl transition-colors active:opacity-60"
            data-active={location.startsWith('/client/comms')}
          >
            <MessageSquare className="w-5 h-5" strokeWidth={1.5} />
            {(unreadMessages as number) > 0 && (
              <span
                aria-hidden="true"
                className="standard-topbar-badge absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 text-black text-[9px] font-black rounded-full flex items-center justify-center"
                style={{ lineHeight: 1 }}
              >
                {(unreadMessages as number) > 99 ? '99+' : unreadMessages}
              </span>
            )}
          </button>
        </Link>}
        <NotificationBell />
        <button onClick={() => setShowMore(!showMore)} aria-label="More app sections" aria-expanded={showMore} className="standard-topbar-apps flex h-9 w-9 shrink-0 items-center justify-center rounded-xl" style={{ color: '#f7f5ef' }}>
          <LayoutGrid className="h-5 w-5" aria-hidden="true" />
        </button>
        {user && (
          <Link href={`/client/members/${user.id}`}>
            <button
              title="My profile"
              aria-label="My profile"
              className="standard-topbar-avatar hidden sm:flex items-center justify-center w-9 h-9 rounded-full transition-all active:opacity-60 ring-1 ring-transparent hover:ring-primary/40"
              style={{ background: 'rgba(255,255,255,0.04)' }}
            >
              <Avatar className="h-7 w-7">
                {user.avatarUrl ? <AvatarImage src={user.avatarUrl} alt="" /> : null}
                <AvatarFallback className="text-[10px] bg-zinc-800 text-zinc-400" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  {(user.fullName ?? user.name ?? user.email ?? 'U').trim().slice(0, 1).toUpperCase()}
                </AvatarFallback>
              </Avatar>
            </button>
          </Link>
        )}
        </>}
      </PremiumSlimBar>

      {/* Push notification opt-in banner — shown once until dismissed or subscribed */}
      <IOSInstallBanner />
      <PushNotificationBanner />
      {!isOnboardingRoute && <ActiveChallengeBanner />}
      <div className="standard-client-workspace">
        {!isOnboardingRoute && <aside className="standard-client-sidebar" aria-label="Main navigation">
          <button type="button" onClick={() => navigate('/client')} aria-current={location === '/client' ? 'page' : undefined}><LayoutDashboard /> My Training</button>
          <button type="button" onClick={() => navigate('/client/plan')} aria-current={location === '/client/plan' ? 'page' : undefined}><ListTodo /> My Plan</button>
          <button type="button" onClick={() => navigate('/client/community')} aria-current={location.startsWith('/client/community') ? 'page' : undefined}><Users /> Community</button>
          <button type="button" onClick={() => navigate('/client/library/vault')} aria-current={location.startsWith('/client/library/vault') ? 'page' : undefined}><BookMarked /> Vault</button>
          {showPrivateCoaching && <button type="button" onClick={() => navigate('/client/comms')} aria-current={isCommsRoute ? 'page' : undefined}><MessageSquare /> Inbox</button>}
          <button type="button" onClick={() => setShowMore(true)}><LayoutGrid /> All tools</button>
        </aside>}
      {/* Scrollable content — momentum scroll on iOS */}
      {/* On comms route, disable outer scroll so iOS keyboard doesn't steal focus */}
      <main
        className={`premium-app-content flex-1 client-app-scroll ${isCommsRoute ? 'premium-inbox-viewport' : isOnboardingRoute ? 'onboarding-layout-viewport' : 'kb-padgap'}`}
        style={isCommsRoute ? {
          overflow: 'hidden',
          minHeight: 0,
        } : isOnboardingRoute ? {
          overflow: 'hidden',
          minHeight: 0,
        } : {
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          overscrollBehaviorY: 'contain',
        }}
      >
        {children}
      </main>
      {/* iPhone More page: covers the screen between the dock and the tab bar
          so the current screen keeps its state underneath. */}
      {phone && showMore && (
        <PhoneMorePage
          user={user}
          location={location}
          hrefFor={hrefFor}
          sections={MORE_SECTIONS.map((section) => {
            const meta = MORE_SECTION_META[section.label as keyof typeof MORE_SECTION_META];
            return { ...section, icon: meta?.icon, description: meta?.description };
          })}
          inPerson={inPersonNavigation}
          showMessages={showPrivateCoaching}
          unreadMessages={unreadMessages as number}
          onClose={() => setShowMore(false)}
          onTour={() => { setShowMore(false); guidedTour.start('client-home', { force: true }); }}
          onSignOut={() => logout.mutate()}
          signingOut={logout.isPending}
        />
      )}
      </div>

      {/* Active Workout floating pill — shown when a workout is in-progress and user navigates away */}
      {!isOnboardingRoute && <ActiveWorkoutPill />}

      {/* More directory — full functionality, organized into calm expandable groups. */}
      {showMore && !phone && (
        <div className="fixed inset-0 z-40" onClick={() => setShowMore(false)}>
          <div className="absolute inset-0 bg-black/50 backdrop-blur-md" />
          <section
            className="absolute bottom-[calc(64px+env(safe-area-inset-bottom,0px))] left-2 right-2 max-h-[78dvh] overflow-hidden rounded-[24px] border border-[#d7dcdf] bg-[#f7f5ef] shadow-[0_26px_80px_rgba(0,0,0,0.28)] slide-up md:bottom-24 md:left-1/2 md:right-auto md:w-[min(760px,calc(100vw-48px))] md:-translate-x-1/2"
            onClick={(event) => event.stopPropagation()}
            aria-label="More app sections"
          >
            <header className="flex items-start justify-between gap-4 border-b border-[#d7dcdf] bg-white/80 px-5 py-4 backdrop-blur-xl">
              <div>
                <p className="t-label text-[#765624]">Everything in one place</p>
                <h2 className="t-title mt-1 text-[#17151a]">More</h2>
                <p className="mt-1 text-xs leading-5 text-[#59636a]">Choose a clear group. Nothing has been removed.</p>
              </div>
              <button
                onClick={() => setShowMore(false)}
                className="grid size-11 shrink-0 place-items-center rounded-full border border-[#d7dcdf] bg-white text-[#59636a] active:scale-95"
                aria-label="Close More"
              >
                <X className="size-5" />
              </button>
            </header>

            <div className="max-h-[calc(78dvh-110px)] overflow-y-auto px-4 py-5 sm:px-5">
              <div>
                <p className="t-label mb-2.5 text-[#765624]">Quick access</p>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                  {[
                    { href: '/client/schedule', label: 'Schedule', icon: Calendar },
                    { href: '/client/nutrition', label: 'Nutrition', icon: Utensils },
                    { href: '/client/library/vault', label: 'Vault', icon: BookMarked },
                    { href: '/client/check-in', label: 'Weekly Review', icon: ClipboardList },
                    { href: '/client/body', label: 'Body Command', icon: Activity },
                  ].map(({ href, label, icon: Icon }) => (
                    <Link key={href} href={href} onClick={() => setShowMore(false)}>
                      <span className="flex min-h-[86px] flex-col items-center justify-center gap-2 rounded-2xl border border-[#d7dcdf] bg-white px-2 text-center text-xs font-black text-[#2f5d73] shadow-[0_8px_20px_rgba(23,21,26,0.04)] active:scale-[0.98]">
                        <Icon className="size-5" />
                        {label}
                      </span>
                    </Link>
                  ))}
                </div>
              </div>

              <div className="mt-5 space-y-3">
                {MORE_SECTIONS.map((section) => {
                  const isExpanded = expandedMoreSection === section.label;
                  const meta = MORE_SECTION_META[section.label as keyof typeof MORE_SECTION_META];
                  const SectionIcon = meta?.icon ?? LayoutGrid;
                  return (
                    <section key={section.label} className="overflow-hidden rounded-2xl border border-[#d7dcdf] bg-white shadow-[0_8px_22px_rgba(23,21,26,0.035)]">
                      <button
                        type="button"
                        onClick={() => setExpandedMoreSection(isExpanded ? null : section.label)}
                        aria-expanded={isExpanded}
                        className="grid w-full grid-cols-[44px_minmax(0,1fr)_auto] items-center gap-3 px-4 py-4 text-left"
                      >
                        <span className="grid size-11 place-items-center rounded-xl bg-[#e7eef1] text-[#2f5d73]">
                          <SectionIcon className="size-5" />
                        </span>
                        <span className="min-w-0">
                          <strong className="block text-base font-black text-[#17151a]">{section.label}</strong>
                          <small className="mt-0.5 block truncate text-xs text-[#68737a]">{meta?.description}</small>
                        </span>
                        <ChevronDown className={`size-5 text-[#78848a] transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                      </button>

                      {isExpanded ? (
                        <div className="border-t border-[#e0e4e6] bg-[#fbfaf7] px-3 py-2">
                          {section.items.map(({ path, label, desc, icon: Icon }) => {
                            const isItemActive = location.startsWith(path);
                            return (
                              <Link key={`${section.label}-${path}-${label}`} href={path} onClick={() => setShowMore(false)}>
                                <span className={`grid min-h-[62px] grid-cols-[38px_minmax(0,1fr)_auto] items-center gap-3 rounded-xl px-2.5 py-2 transition-colors ${isItemActive ? 'bg-[#e7eef1]' : 'hover:bg-[#f1f2ef]'}`}>
                                  <span className="grid size-9 place-items-center rounded-lg border border-[#d7dcdf] bg-white text-[#2f5d73]">
                                    <Icon className="size-4.5" strokeWidth={1.8} />
                                  </span>
                                  <span className="min-w-0">
                                    <strong className="block truncate text-sm font-black text-[#202126]">{label}</strong>
                                    <small className="mt-0.5 block truncate text-xs text-[#68737a]">{desc}</small>
                                  </span>
                                  <ChevronRight className="size-4 text-[#879197]" />
                                </span>
                              </Link>
                            );
                          })}
                        </div>
                      ) : null}
                    </section>
                  );
                })}
              </div>

              {user ? (
                <Link href={`/client/members/${user.id}`} onClick={() => setShowMore(false)}>
                  <span className="mt-4 grid min-h-[64px] grid-cols-[44px_minmax(0,1fr)_auto] items-center gap-3 rounded-2xl border border-[#d7dcdf] bg-white px-4 py-3">
                    <span className="grid size-11 place-items-center overflow-hidden rounded-xl bg-[#eee5d4] text-[#765624]">
                      {user.avatarUrl ? <img src={user.avatarUrl} alt="" className="size-full object-cover" /> : <UserCircle className="size-5" />}
                    </span>
                    <span><strong className="block text-sm font-black text-[#17151a]">My Profile</strong><small className="text-xs text-[#68737a]">Wall, photos, badges, and account</small></span>
                    <ChevronRight className="size-4 text-[#879197]" />
                  </span>
                </Link>
              ) : null}

              <div className="mt-4 grid grid-cols-2 gap-2">
                <PushToggleButton />
                <button
                  onClick={() => { setShowMore(false); guidedTour.start('client-home', { force: true }); }}
                  className="flex min-h-[58px] items-center justify-center gap-2 rounded-xl border border-[#d7dcdf] bg-white px-3 text-xs font-black text-[#2f5d73] active:scale-[0.98]"
                >
                  <Sparkles className="size-4" /> Show Me Around
                </button>
              </div>

              <button
                onClick={() => logout.mutate()}
                disabled={logout.isPending}
                className="mt-4 flex min-h-[52px] w-full items-center justify-center gap-2 rounded-xl border border-[#dfc4c0] bg-[#fbf3f2] text-sm font-black text-[#a34e46] disabled:opacity-50"
              >
                <LogOut className="size-4" />
                {logout.isPending ? 'Signing out…' : 'Sign Out'}
              </button>
            </div>
          </section>
        </div>
      )}

      {/* Phone: Today, Training, Nutrition, Faith, and More. iPad: five fixed
          destinations, with the full directory in the header. */}
      {!isOnboardingRoute && user && (
        <ClientCustomBottomNav
          location={location}
          userId={user.id}
          inPerson={inPersonNavigation}
          mapHref={hrefFor}
          phone={phone}
          moreOpen={showMore}
          onMore={() => setShowMore((open) => !open)}
          onNavigate={() => setShowMore(false)}
        />
      )}
      </div>
    </div>
  );
}
